import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Edit2, Trash2, Package, Clock, AlertTriangle, HelpCircle, Users } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { AxiosError } from 'axios';
import { useSearch } from 'wouter';

import { bookingService } from '@/services/api/booking.service';
import { organizationService } from '@/services/api/organization.service';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { StepProgress } from '@/components/ui/step-progress';
import { SearchBar } from '@/components/ui/search-bar';
import { MultiSelect } from '@/components/ui/multi-select';
import { FloatingActionButton } from '@/components/ui/floating-action-button';
import { useToast } from '@/hooks/use-toast';
import { formatCurrency, getCurrencySymbol, type Currency } from '@/utils/currency';
import type { Service, CustomQuestion } from '@/types/booking';
import type { SubscriptionError } from '@/types/subscription';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useSubscription } from '@/contexts/subscription-context';
import { UpgradeDialog } from '@/components/subscription/upgrade-dialog';
import { UpgradeNudge } from '@/components/subscription/UpgradeNudge';
import { isSubscriptionError, extractSubscriptionError } from '@/lib/subscription-error-handler';
import { sanitizeErrorMessage } from '@/lib/error-utils';
import { useSetPageHeader } from '@/components/chrome/PageHeader';
import { LimitUsageIndicator } from '@/components/subscription/LimitUsageIndicator';
import { CustomQuestionsEditor, validateCustomQuestions } from '@/components/services/CustomQuestionsEditor';
import { MarkdownDescription } from '@/components/MarkdownDescription';
import { cn } from '@/lib/utils';

const serviceSchema = z.object({
  name: z.string().min(1, 'Service name is required').max(100, 'Name must be less than 100 characters'),
  description: z.string().max(500, 'Description must be less than 500 characters').optional(),
  duration: z.number().min(1, 'Duration must be at least 1 minute').max(480, 'Duration cannot exceed 8 hours'),
  price: z.number().min(0, 'Price must be non-negative').or(z.nan()),
  maxCapacity: z.number().min(1, 'Capacity must be at least 1').max(100, 'Capacity cannot exceed 100'),
  isGroupService: z.boolean(),
  waitlistEnabled: z.boolean(),
  active: z.boolean(),
  hidePrice: z.boolean(),
});

type ServiceFormData = z.infer<typeof serviceSchema>;

interface DurationImpact {
  future_unbooked: number;
  future_booked: number;
  past: number;
  can_update: boolean;
}

const WIZARD_STEP_COUNT = 3;

const WIZARD_STEP_KEYS: Record<number, string> = {
  1: 'wizard.steps.serviceDetails',
  2: 'wizard.steps.teamMembers',
  3: 'wizard.steps.customQuestions',
};

/** With `skipTeam` the team-members step (2) is left out, so step 3 shows as 2 of 2. */
function StepIndicator({ currentStep, skipTeam = false }: { currentStep: number; skipTeam?: boolean }) {
  const { t } = useTranslation('services');
  const labelKey = WIZARD_STEP_KEYS[currentStep] ?? WIZARD_STEP_KEYS[1];
  const shownStep = skipTeam && currentStep === 3 ? 2 : currentStep;
  return (
    <StepProgress
      currentStep={shownStep}
      totalSteps={skipTeam ? WIZARD_STEP_COUNT - 1 : WIZARD_STEP_COUNT}
      label={t(labelKey)}
    />
  );
}

function ServiceDetailsStep({ form, currency, t }: { form: any; currency: Currency; t: any }) {
  return (
    <>
      <FormField
        control={form.control}
        name="name"
        render={({ field }: { field: any }) => (
          <FormItem>
            <FormLabel>{t('form.name')}</FormLabel>
            <FormControl>
              <Input placeholder={t('form.namePlaceholder')} {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="description"
        render={({ field }: { field: any }) => (
          <FormItem>
            <FormLabel>{t('form.description')}</FormLabel>
            <FormControl>
              <Textarea
                placeholder={t('form.descriptionPlaceholder')}
                rows={4}
                {...field}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <div className="grid grid-cols-2 gap-4">
        <FormField
          control={form.control}
          name="duration"
          render={({ field }: { field: any }) => (
            <FormItem>
              <FormLabel>{t('form.duration')}</FormLabel>
              <FormControl>
                <Input
                  type="number"
                  min="1"
                  max="480"
                  placeholder={t('form.durationPlaceholder')}
                  value={field.value || ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => field.onChange(parseInt(e.target.value) || 0)}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="price"
          render={({ field }: { field: any }) => (
            <FormItem>
              <FormLabel>{t('form.price')}</FormLabel>
              <FormControl>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-soft">
                    {getCurrencySymbol(currency)}
                  </span>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="0.00"
                    className="pl-8"
                    value={isNaN(field.value) ? '' : field.value}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      const value = e.target.value;
                      field.onChange(value === '' ? NaN : parseFloat(value));
                    }}
                  />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      <FormField
        control={form.control}
        name="isGroupService"
        render={({ field }: { field: any }) => (
          <FormItem className="flex items-center justify-between">
            <div className="space-y-0.5">
              <FormLabel>{t('form.groupService')}</FormLabel>
              <p className="text-xs text-soft">
                {t('form.groupServiceDescription')}
              </p>
            </div>
            <FormControl>
              <Switch
                checked={field.value}
                onCheckedChange={(checked: boolean) => {
                  field.onChange(checked);
                  if (!checked) form.setValue('maxCapacity', 1);
                }}
              />
            </FormControl>
          </FormItem>
        )}
      />

      {form.watch('isGroupService') && (
        <>
          <FormField
            control={form.control}
            name="maxCapacity"
            render={({ field }: { field: any }) => (
              <FormItem>
                <FormLabel>{t('form.maxCapacity')}</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    min="1"
                    max="100"
                    placeholder={t('form.maxCapacityPlaceholder')}
                    value={field.value ?? ''}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      const v = e.target.value;
                      const n = parseInt(v, 10);
                      // null, not undefined — RHF's Controller falls back to the
                      // field defaultValue when given undefined, which makes the
                      // input impossible to clear (it snaps back to 1).
                      field.onChange(v === '' || Number.isNaN(n) ? null : n);
                    }}
                    onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
                      const n = parseInt(e.target.value, 10);
                      field.onChange(Number.isNaN(n) || n < 1 ? 1 : n > 100 ? 100 : n);
                    }}
                  />
                </FormControl>
                <p className="text-xs text-soft">
                  {t('form.maxCapacityDescription')}
                </p>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="waitlistEnabled"
            render={({ field }: { field: any }) => {
              const capacity = Number(form.watch('maxCapacity')) || 1;
              const canEnable = capacity > 1;
              const effectiveValue = canEnable ? field.value : false;
              return (
                <FormItem className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <FormLabel>{t('form.waitlistEnabled', 'Enable waitlist')}</FormLabel>
                    <p className="text-xs text-soft">
                      {t('form.waitlistEnabledDescription', "When the class is full, customers can join a waitlist and are auto-promoted when a spot opens.")}
                    </p>
                    {!canEnable && (
                      <p className="text-xs text-amber-600">
                        {t('form.waitlistRequiresGroup', 'Waitlist is only available when capacity is greater than 1.')}
                      </p>
                    )}
                  </div>
                  <FormControl>
                    <Switch
                      checked={effectiveValue}
                      disabled={!canEnable}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              );
            }}
          />
        </>
      )}

      <FormField
        control={form.control}
        name="hidePrice"
        render={({ field }: { field: any }) => (
          <FormItem className="flex items-center justify-between">
            <div className="space-y-0.5">
              <FormLabel>{t('form.hidePrice')}</FormLabel>
              <p className="text-xs text-soft">
                {t('form.hidePriceDescription')}
              </p>
            </div>
            <FormControl>
              <Switch
                checked={field.value}
                onCheckedChange={field.onChange}
              />
            </FormControl>
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="active"
        render={({ field }: { field: any }) => (
          <FormItem className="flex items-center justify-between">
            <FormLabel>{t('form.active')}</FormLabel>
            <FormControl>
              <Switch
                checked={field.value}
                onCheckedChange={field.onChange}
              />
            </FormControl>
          </FormItem>
        )}
      />
    </>
  );
}

function TeamMembersStep({
  providers,
  selectedProviderIds,
  onChangeProviderIds,
  t,
}: {
  providers: any[];
  selectedProviderIds: number[];
  onChangeProviderIds: (ids: number[]) => void;
  t: any;
}) {
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Users className="h-5 w-5 text-soft" />
        <h3 className="text-h3 text-ink">{t('services:wizard.teamMembers')}</h3>
        <Badge tone="neutral">{t('services:wizard.selected', { count: selectedProviderIds.length })}</Badge>
      </div>
      <p className="text-sm text-soft">
        {t('services:wizard.teamMembersHint')}
      </p>
      <MultiSelect
        options={providers.filter((p: any) => p.active).map((p: any) => ({
          label: p.displayName || p.name,
          value: p.id,
        }))}
        value={selectedProviderIds}
        onChange={onChangeProviderIds}
        placeholder={t('services:wizard.teamMembersPlaceholder')}
        emptyMessage={t('services:wizard.teamMembersEmpty')}
      />
    </div>
  );
}

function CustomQuestionsStep({
  customQuestions,
  onChangeQuestions,
  t,
}: {
  customQuestions: CustomQuestion[];
  onChangeQuestions: (questions: CustomQuestion[]) => void;
  t: any;
}) {
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <HelpCircle className="h-5 w-5 text-soft" />
        <h3 className="text-h3 text-ink">{t('services:customQuestions.title')}</h3>
        <Badge tone="neutral">{customQuestions.length}/3</Badge>
      </div>
      <p className="text-sm text-soft">
        {t('services:customQuestions.description')}
      </p>
      <CustomQuestionsEditor
        questions={customQuestions}
        onChange={onChangeQuestions}
      />
    </div>
  );
}

function WizardFooter({
  currentStep,
  onBack,
  onNext,
  onCancel,
  isPending,
  submitLabel,
  nextDisabled,
  t,
}: {
  currentStep: number;
  onBack: () => void;
  onNext: () => void;
  onCancel: () => void;
  isPending: boolean;
  submitLabel: string;
  nextDisabled?: boolean;
  t: any;
}) {
  return (
    <div className="flex justify-between pt-4">
      <div>
        {currentStep === 1 ? (
          <Button type="button" variant="outline" onClick={onCancel}>
            {t('common:actions.cancel')}
          </Button>
        ) : (
          <Button type="button" variant="outline" onClick={onBack}>
            {t('common:actions.back')}
          </Button>
        )}
      </div>
      <div>
        {currentStep < 3 ? (
          <Button key="next" type="button" onClick={onNext} disabled={nextDisabled}>
            {t('common:actions.next')}
          </Button>
        ) : (
          <Button key="submit" type="submit" disabled={isPending}>
            {isPending ? submitLabel + '...' : submitLabel}
          </Button>
        )}
      </div>
    </div>
  );
}

export default function AdminServicesPage() {
  const [searchTerm, setSearchTerm] = useState('');
  const [showInactive, setShowInactive] = useState(true);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [selectedService, setSelectedService] = useState<Service | null>(null);
  const [showDurationWarning, setShowDurationWarning] = useState(false);
  const [durationImpact, setDurationImpact] = useState<DurationImpact | null>(null);
  const [upgradeError, setUpgradeError] = useState<SubscriptionError | null>(null);
  const [showUpgradeNudge, setShowUpgradeNudge] = useState(false);
  const [pendingFormData, setPendingFormData] = useState<ServiceFormData | null>(null);
  const [customQuestions, setCustomQuestions] = useState<CustomQuestion[]>([]);
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedProviderIds, setSelectedProviderIds] = useState<number[]>([]);

  const { t } = useTranslation(['services', 'common', 'subscription']);
  const { toast} = useToast();
  const queryClient = useQueryClient();
  const { canPerformAction, limits } = useSubscription();
  const searchString = useSearch();

  useSetPageHeader(
    {
      title: t('title'),
      sub: t('subtitle'),
      actions: (
        <Button
          className="hidden md:inline-flex"
          data-testid="create-service-btn"
          size="md"
          onClick={() => canPerformAction('services') ? setIsCreateDialogOpen(true) : setShowUpgradeNudge(true)}
          iconLeft={<Plus className="h-4 w-4" />}
        >
          <span>{t('create.button')}</span>
          {limits && (
            <span className="ml-1.5 rounded-full bg-white/20 px-1.5 py-0.5 text-[11px] font-medium leading-none tabular-nums">
              {limits.usage.services.current}/{limits.usage.services.limit}
            </span>
          )}
        </Button>
      ),
    },
    [t, canPerformAction, limits],
  );

  const form = useForm<ServiceFormData>({
    resolver: zodResolver(serviceSchema),
    defaultValues: {
      name: '',
      description: '',
      duration: 30,
      price: NaN,
      maxCapacity: 1,
      isGroupService: false,
      waitlistEnabled: false,
      active: true,
      hidePrice: false,
    },
  });

  const watchedName = form.watch('name');

  // Fetch services
  const { data: services = [], isLoading } = useQuery({
    queryKey: ['services'],
    queryFn: () => bookingService.getServices(),
  });

  // Fetch organization to get currency
  const { data: organization } = useQuery({
    queryKey: ['organization'],
    queryFn: () => organizationService.getOrganization(),
  });

  const currency = (organization?.currency as Currency) || 'USD';

  // Fetch providers for team member selection
  const { data: providers = [] } = useQuery({
    queryKey: ['providers'],
    queryFn: () => bookingService.getProviders(),
  });

  // A one-person practice has nothing to choose in the team step: the only
  // active provider is assigned automatically and the step is skipped.
  const activeProviders = providers.filter((p) => p.active);
  const soloProvider = activeProviders.length === 1 ? activeProviders[0] : null;
  const goNext = () => setCurrentStep((s) => (soloProvider && s === 1 ? 3 : s + 1));
  const goBack = () => setCurrentStep((s) => (soloProvider && s === 3 ? 1 : s - 1));

  useEffect(() => {
    if (!soloProvider || !(isCreateDialogOpen || isEditDialogOpen)) return;
    if (selectedProviderIds.length === 0) setSelectedProviderIds([soloProvider.id]);
  }, [soloProvider, isCreateDialogOpen, isEditDialogOpen, selectedProviderIds.length]);

  // Handle deep-linking from URL parameters (e.g., from custom-questions page)
  useEffect(() => {
    if (!services.length) return;

    const params = new URLSearchParams(searchString);
    const editServiceId = params.get('editService');
    const expandQuestions = params.get('expandQuestions');

    if (editServiceId) {
      const service = services.find(s => s.id === parseInt(editServiceId));
      if (service) {
        handleEdit(service);
        if (expandQuestions === 'true') {
          setCurrentStep(3);
        }
        // Clear URL params after handling
        window.history.replaceState({}, '', window.location.pathname);
      }
    }
  }, [services, searchString]);

  // Filter services by search term and active status
  const filteredServices = services.filter(service => {
    const matchesSearch = service.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      service.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = showInactive || service.active;
    return matchesSearch && matchesStatus;
  });

  // Create service mutation
  const createServiceMutation = useMutation({
    mutationFn: (data: Omit<Service, 'id' | 'createdAt' | 'updatedAt'>) =>
      bookingService.createService(data),
    onSuccess: () => {
      toast({ title: t('common:toasts.createSuccess'), description: t('create.success') });
      queryClient.invalidateQueries({ queryKey: ['services'] });
      queryClient.invalidateQueries({ queryKey: ['subscription', 'limits'] });
      queryClient.invalidateQueries({ queryKey: ['providers'] });
      setIsCreateDialogOpen(false);
      form.reset();
      setCurrentStep(1);
      setSelectedProviderIds([]);
      setCustomQuestions([]);
    },
    onError: (error: any) => {
      // Check if it's a subscription error first
      if (isSubscriptionError(error)) {
        const subError = extractSubscriptionError(error as AxiosError);
        if (subError) {
          setUpgradeError(subError);
          return;
        }
      }

      // Check if it's a validation error (422) with field-specific errors
      if (error.response?.status === 422) {
        const errors = error.response?.data?.error?.errors || [];
        const errorMessage = Array.isArray(errors) ? errors[0] : errors;

        // Check if it's a name uniqueness error
        if (errorMessage && typeof errorMessage === 'string' &&
            errorMessage.toLowerCase().includes('name') &&
            errorMessage.toLowerCase().includes('already exists')) {
          form.setError('name', {
            type: 'manual',
            message: errorMessage,
          });
        } else {
          // For other 422 errors, show all validation errors
          toast({
            title: t('toasts.validationErrorTitle'),
            description: Array.isArray(errors) ? errors.join(', ') : (errorMessage || t('create.error')),
            variant: 'destructive',
          });
        }
      } else {
        // For non-422 errors, show toast with sanitized message
        toast({
          title: t('common:errors.generic'),
          description: sanitizeErrorMessage(error, t('create.error')),
          variant: 'destructive',
        });
      }
    },
  });

  // Update service mutation
  const updateServiceMutation = useMutation({
    mutationFn: ({ id, updates }: { id: number; updates: Partial<Service> }) =>
      bookingService.updateService(id, updates),
    onSuccess: () => {
      toast({ title: t('common:toasts.updateSuccess'), description: t('edit.success') });
      queryClient.invalidateQueries({ queryKey: ['services'] });
      queryClient.invalidateQueries({ queryKey: ['providers'] });
      setIsEditDialogOpen(false);
      setSelectedService(null);
      form.reset();
      setCurrentStep(1);
      setSelectedProviderIds([]);
      setCustomQuestions([]);
    },
    onError: (error: any) => {
      // Check if it's a validation error (422) with field-specific errors
      if (error.response?.status === 422) {
        const errors = error.response?.data?.error?.errors || [];
        const errorMessage = Array.isArray(errors) ? errors[0] : errors;

        // Check if it's a name uniqueness error
        if (errorMessage && typeof errorMessage === 'string' &&
            errorMessage.toLowerCase().includes('name') &&
            errorMessage.toLowerCase().includes('already exists')) {
          form.setError('name', {
            type: 'manual',
            message: errorMessage,
          });
        } else {
          // For other 422 errors, show all validation errors
          toast({
            title: t('toasts.validationErrorTitle'),
            description: Array.isArray(errors) ? errors.join(', ') : (errorMessage || t('edit.error')),
            variant: 'destructive',
          });
        }
      } else {
        // For non-422 errors, show toast with sanitized message
        toast({
          title: t('common:errors.generic'),
          description: sanitizeErrorMessage(error, t('edit.error')),
          variant: 'destructive',
        });
      }
    },
  });

  // Delete service mutation
  const deleteServiceMutation = useMutation({
    mutationFn: (serviceId: number) => bookingService.deleteService(serviceId),
    onSuccess: () => {
      toast({ title: t('common:toasts.deleteSuccess'), description: t('delete.success') });
      queryClient.invalidateQueries({ queryKey: ['services'] });
      queryClient.invalidateQueries({ queryKey: ['subscription', 'limits'] });
    },
    onError: (error: any) => {
      toast({
        title: t('common:errors.generic'),
        description: sanitizeErrorMessage(error, t('delete.error')),
        variant: 'destructive',
      });
    },
  });

  const onSubmit = async (data: ServiceFormData) => {
    // Validate custom questions before submission
    const questionsError = validateCustomQuestions(customQuestions, t);
    if (questionsError) {
      toast({
        title: t('services:customQuestions.validation.title'),
        description: questionsError,
        variant: 'destructive',
      });
      return;
    }

    const effectiveCapacity = data.isGroupService ? data.maxCapacity : 1;
    const effectiveWaitlist = data.isGroupService && data.waitlistEnabled && effectiveCapacity > 1;
    const { isGroupService: _isGroupService, waitlistEnabled: _waitlistEnabled, ...rest } = data;
    const submitData = {
      ...rest,
      maxCapacity: effectiveCapacity,
      price: isNaN(data.price) ? 0 : data.price,
      max_capacity: effectiveCapacity,
      hide_price: data.hidePrice,
      waitlist_enabled: effectiveWaitlist,
      custom_booking_questions: customQuestions,
      provider_ids: selectedProviderIds,
    };

    // Check if we're editing and duration has changed
    if (selectedService && selectedService.duration !== data.duration) {
      try {
        // Check impact of duration change
        const response = await bookingService.checkDurationImpact(selectedService.id, data.duration);

        if (!response.can_update) {
          // Show warning modal
          setDurationImpact(response);
          setPendingFormData(data);
          setShowDurationWarning(true);
          return;
        }
      } catch (error) {
        console.error('Error checking duration impact:', error);
        // Continue with update if check fails
      }
    }

    // Proceed with mutation
    if (selectedService) {
      updateServiceMutation.mutate({
        id: selectedService.id,
        updates: submitData,
      });
    } else {
      createServiceMutation.mutate(submitData as any);
    }
  };

  const handleDurationWarningClose = () => {
    setShowDurationWarning(false);
    setDurationImpact(null);
    setPendingFormData(null);
  };

  const handleEdit = (service: Service) => {
    setSelectedService(service);
    form.reset({
      name: service.name,
      description: service.description || '',
      duration: service.duration,
      price: service.price,
      maxCapacity: service.max_capacity || service.maxCapacity || 1,
      isGroupService: (service.max_capacity || service.maxCapacity || 1) > 1,
      waitlistEnabled: service.waitlist_enabled || service.waitlistEnabled || false,
      active: service.active,
      hidePrice: service.hide_price || false,
    });
    // Load custom questions from service
    const questions = service.custom_booking_questions || service.customBookingQuestions || [];
    setCustomQuestions(questions);
    // Populate provider IDs from service
    setSelectedProviderIds(service.provider_ids || []);
    setCurrentStep(1);
    setIsEditDialogOpen(true);
  };

  const handleDelete = (serviceId: number) => {
    if (confirm(t('delete.confirm') + ' ' + t('delete.warning'))) {
      deleteServiceMutation.mutate(serviceId);
    }
  };

  const formatDuration = (minutes: number) => {
    if (minutes < 60) {
      return `${minutes}m`;
    }
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
  };

  return (
    <div className="space-y-6">
      {/* Create Service Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{t('create.title')}</DialogTitle>
            </DialogHeader>

            {/* Step Indicator */}
            <StepIndicator currentStep={currentStep} skipTeam={Boolean(soloProvider)} />

            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                {currentStep === 1 && (
                  <ServiceDetailsStep form={form} currency={currency} t={t} />
                )}

                {currentStep === 2 && (
                  <TeamMembersStep
                    providers={providers}
                    selectedProviderIds={selectedProviderIds}
                    onChangeProviderIds={setSelectedProviderIds}
                    t={t}
                  />
                )}

                {currentStep === 3 && (
                  <CustomQuestionsStep
                    customQuestions={customQuestions}
                    onChangeQuestions={setCustomQuestions}
                    t={t}
                  />
                )}

                <WizardFooter
                  currentStep={currentStep}
                  onBack={goBack}
                  onNext={goNext}
                  onCancel={() => {
                    setIsCreateDialogOpen(false);
                    setCustomQuestions([]);
                    setCurrentStep(1);
                    setSelectedProviderIds([]);
                  }}
                  isPending={createServiceMutation.isPending}
                  submitLabel={t('create.button')}
                  nextDisabled={
                    (currentStep === 1 && !watchedName?.trim()) ||
                    (currentStep === 2 && selectedProviderIds.length === 0)
                  }
                  t={t}
                />
              </form>
            </Form>
          </DialogContent>
      </Dialog>

      {/* Search and Stats */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        <div className="lg:col-span-3">
          <Card className="h-full">
            <CardContent className="p-4 h-full flex items-center">
              <SearchBar
                containerClassName="w-full"
                placeholder={t('common:actions.search') + ' ' + t('title').toLowerCase() + '...'}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                data-testid="search-services"
                actions={
                  <div className="flex flex-col items-center gap-1">
                    <Label htmlFor="show-inactive" className="whitespace-nowrap text-xs">
                      {t('filter.showInactive')}
                    </Label>
                    <Switch
                      id="show-inactive"
                      checked={showInactive}
                      onCheckedChange={setShowInactive}
                      data-testid="show-inactive-toggle"
                    />
                  </div>
                }
              />
            </CardContent>
          </Card>
        </div>
        <Card className="h-full">
          <CardContent className="p-4 h-full flex items-center">
            <div className="flex items-center justify-between w-full">
              <div>
                <p className="text-sm text-soft">{t('title')}</p>
                <p className="text-2xl font-bold">{services.length}</p>
              </div>
              <Package className="h-8 w-8 text-brand-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Limit Usage Indicator */}
      {limits && (
        <LimitUsageIndicator
          limitName={t('subscription:limitIndicator.serviceLimit')}
          current={limits.usage.services.current}
          max={limits.usage.services.limit}
          isUnlimited={limits.usage.services.unlimited}
          entityName={t('subscription:limitIndicator.services')}
        />
      )}

      {/* Services List */}
      {isLoading ? (
        <Card>
          <CardContent className="p-6">
            <div className="space-y-3">
              {Array.from({ length: 6 }).map((_, index) => (
                <div key={index} className="flex items-center gap-4 animate-pulse">
                  <div className="h-4 bg-surface-2 rounded flex-1"></div>
                  <div className="h-4 bg-surface-2 rounded w-16"></div>
                  <div className="h-4 bg-surface-2 rounded w-20"></div>
                  <div className="h-4 bg-surface-2 rounded w-16"></div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      ) : filteredServices.length === 0 ? (
        <Card>
          <CardContent className="py-12">
            <div className="text-center">
              <Package className="h-12 w-12 text-soft mx-auto mb-4" />
              <h3 className="text-h3 text-ink mb-2">
                {searchTerm ? t('common:errors.notFound') : t('list.empty')}
              </h3>
              <p className="text-soft mb-4">
                {searchTerm ? t('common:actions.search') : t('list.emptyDescription')}
              </p>
              {!searchTerm && (
                <Button onClick={() => setIsCreateDialogOpen(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  {t('create.button')}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-line bg-surface-2">
                    <th scope="col" className="px-4 py-3 text-left font-semibold text-soft whitespace-nowrap">
                      {t('list.columns.name')}
                    </th>
                    <th scope="col" className="px-4 py-3 text-left font-semibold text-soft whitespace-nowrap hidden md:table-cell">
                      {t('list.columns.duration')}
                    </th>
                    <th scope="col" className="px-4 py-3 text-left font-semibold text-soft whitespace-nowrap hidden md:table-cell">
                      {t('list.columns.price')}
                    </th>
                    <th scope="col" className="px-4 py-3 text-left font-semibold text-soft whitespace-nowrap hidden md:table-cell">
                      {t('list.columns.capacity')}
                    </th>
                    <th scope="col" className="px-4 py-3 text-left font-semibold text-soft whitespace-nowrap hidden sm:table-cell">
                      {t('list.columns.status')}
                    </th>
                    <th scope="col" className="px-4 py-3 text-right">
                      <span className="sr-only">{t('list.columns.actions')}</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredServices.map((service) => (
                    <tr
                      key={service.id}
                      onClick={() => handleEdit(service)}
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleEdit(service);
                        }
                      }}
                      className={cn(
                        'border-b border-line last:border-0 cursor-pointer transition-colors',
                        'hover:bg-surface-2 focus:outline-none focus:bg-surface-2',
                        !service.active && 'opacity-60'
                      )}
                    >
                      <td className="px-4 py-3">
                        <div className="flex flex-col min-w-0">
                          <span className="font-medium text-ink break-words">
                            {service.name}
                          </span>
                          {service.description && (
                            <MarkdownDescription
                              content={service.description}
                              className="text-xs text-soft line-clamp-2"
                            />
                          )}
                          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 md:hidden text-xs text-soft">
                            <span className="flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {formatDuration(service.duration)}
                            </span>
                            <span className="font-medium text-green-600">
                              {formatCurrency(service.price, currency)}
                            </span>
                            <Badge tone={service.active ? 'success' : 'neutral'} className="sm:hidden">
                              {service.active ? t('common:status.active') : t('common:status.inactive')}
                            </Badge>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell">
                        <div className="flex items-center text-soft">
                          <Clock className="h-4 w-4 mr-1" />
                          {formatDuration(service.duration)}
                        </div>
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell font-medium text-green-600">
                        {formatCurrency(service.price, currency)}
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell">
                        {(() => {
                          const capacity = service.max_capacity || service.maxCapacity || 1;
                          return capacity > 1 ? (
                            <div className="flex items-center text-soft">
                              <Users className="h-4 w-4 mr-1" />
                              {capacity}
                            </div>
                          ) : null;
                        })()}
                      </td>
                      <td className="px-4 py-3 hidden sm:table-cell">
                        <Badge tone={service.active ? 'success' : 'neutral'}>
                          {service.active ? t('common:status.active') : t('common:status.inactive')}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEdit(service)}
                            className="min-h-[36px] min-w-[36px] p-2"
                            data-testid={`edit-service-${service.id}`}
                          >
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDelete(service.id)}
                            className="min-h-[36px] min-w-[36px] p-2"
                            data-testid={`delete-service-${service.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Edit Service Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t('edit.title')}</DialogTitle>
          </DialogHeader>

          {/* Step Indicator */}
          <StepIndicator currentStep={currentStep} skipTeam={Boolean(soloProvider)} />

          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              {currentStep === 1 && (
                <ServiceDetailsStep form={form} currency={currency} t={t} />
              )}

              {currentStep === 2 && (
                <TeamMembersStep
                  providers={providers}
                  selectedProviderIds={selectedProviderIds}
                  onChangeProviderIds={setSelectedProviderIds}
                  t={t}
                />
              )}

              {currentStep === 3 && (
                <CustomQuestionsStep
                  customQuestions={customQuestions}
                  onChangeQuestions={setCustomQuestions}
                  t={t}
                />
              )}

              <WizardFooter
                currentStep={currentStep}
                onBack={goBack}
                onNext={goNext}
                onCancel={() => {
                  setIsEditDialogOpen(false);
                  setSelectedService(null);
                  setCustomQuestions([]);
                  setCurrentStep(1);
                  setSelectedProviderIds([]);
                }}
                isPending={updateServiceMutation.isPending}
                submitLabel={t('common:actions.save')}
                nextDisabled={
                  (currentStep === 1 && !watchedName?.trim()) ||
                  (currentStep === 2 && selectedProviderIds.length === 0)
                }
                t={t}
              />
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* Duration Change Warning Modal */}
      <Dialog open={showDurationWarning} onOpenChange={handleDurationWarningClose}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <div className="flex items-center space-x-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              <DialogTitle>{t('durationWarning.title')}</DialogTitle>
            </div>
            <DialogDescription>
              {t('durationWarning.description')}
            </DialogDescription>
          </DialogHeader>

          {durationImpact && (
            <div className="space-y-3 py-4">
              {durationImpact.future_booked > 0 && (
                <Alert variant="destructive">
                  <AlertDescription>
                    <strong>{durationImpact.future_booked}</strong> {t('durationWarning.futureBooked')}
                  </AlertDescription>
                </Alert>
              )}

              {durationImpact.future_unbooked > 0 && (
                <Alert>
                  <AlertDescription>
                    <strong>{durationImpact.future_unbooked}</strong> {t('durationWarning.futureUnbooked')}
                  </AlertDescription>
                </Alert>
              )}

              <div className="text-sm text-soft space-y-1">
                <p>{t('durationWarning.instruction')}</p>
                <ul className="list-disc list-inside pl-2 space-y-1">
                  {durationImpact.future_booked > 0 && (
                    <li>{t('durationWarning.step1')}</li>
                  )}
                  <li>{t('durationWarning.step2')}</li>
                </ul>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={handleDurationWarningClose}
            >
              {t('common:actions.close')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upgrade Dialog */}
      {upgradeError && (
        <UpgradeDialog
          open={!!upgradeError}
          onClose={() => setUpgradeError(null)}
          error={upgradeError}
        />
      )}

      {/* Upgrade Nudge Dialog */}
      <UpgradeNudge
        isOpen={showUpgradeNudge}
        onClose={() => setShowUpgradeNudge(false)}
        feature="services"
      />

      {/* Mobile FAB for adding service */}
      <FloatingActionButton
        icon={<Plus className="w-5 h-5" />}
        label={t('create.button')}
        onClick={() => canPerformAction('services') ? setIsCreateDialogOpen(true) : setShowUpgradeNudge(true)}
      />
    </div>
  );
}